import React from 'react';
import { motion } from 'framer-motion';
import {
  SparklesIcon,
  DocumentArrowUpIcon,
  CpuChipIcon,
  BoltIcon,
  DocumentDuplicateIcon,
  PrinterIcon,
  AcademicCapIcon,
  CloudArrowUpIcon,
  DocumentPlusIcon,
  LockClosedIcon,
  QueueListIcon,
  EyeIcon,
  ArchiveBoxIcon,
  ScissorsIcon
} from '@heroicons/react/24/outline';

const Features: React.FC = () => {
  // Core Features - aligned with actual app functionality
  const coreFeatures = [
    { 
      icon: SparklesIcon, 
      title: 'Invert Colors', 
      description: 'Transform dark slides to white backgrounds',
      details: 'Perfect for lecture slides with dark backgrounds. Instantly converts black backgrounds to white, making notes print-friendly and easy to read.'
    },
    { 
      icon: DocumentArrowUpIcon, 
      title: 'Clear Background', 
      description: 'Remove unwanted backgrounds completely',
      details: 'Eliminates distracting backgrounds while preserving text clarity. Ideal for creating clean, professional documents from any source.'
    },
    { 
      icon: DocumentPlusIcon, 
      title: 'Merge PDFs', 
      description: 'Combine multiple PDFs into one document',
      details: 'Merge multiple PDF files into a single document seamlessly. Perfect for combining notes from different lectures or organizing study materials.'
    },
    { 
      icon: DocumentDuplicateIcon, 
      title: 'Multiple Slides Per Page', 
      description: 'Arrange 1-4 slides per page in custom layouts',
      details: 'Save paper and create compact study guides. Choose portrait or landscape orientation with flexible row/column arrangements.'
    },
    { 
      icon: BoltIcon, 
      title: 'Remove Unwanted Slides', 
      description: 'Deselect and remove specific slides easily',
      details: 'Cherry-pick only the slides you need. Remove title slides, duplicates, or irrelevant pages with a simple click.'
    },
    { 
      icon: CpuChipIcon, 
      title: 'Black & White Conversion', 
      description: 'Pure grayscale for optimal printing',
      details: 'Convert colorful slides to crisp black and white. Reduces ink usage and ensures perfect print quality on any printer.'
    },
    { 
      icon: LockClosedIcon, 
      title: '100% Browser-Based', 
      description: 'Your files never leave your device',
      details: 'All processing happens locally in your browser. No uploads, no servers, no data collection. Complete privacy guaranteed.'
    },
    { 
      icon: PrinterIcon, 
      title: 'Print-Optimized Output', 
      description: 'Professional DPI and margins for high-quality printing',
      details: 'Automatically optimized for physical printing with proper margins, high DPI, and perfect page layout for binding or folders.'
    },
    { 
      icon: QueueListIcon, 
      title: 'Batch Processing', 
      description: 'Process multiple PDFs simultaneously',
      details: 'Handle multiple files at once with our efficient batch processing system. Save time when working with large collections of documents.'
    },
    { 
      icon: EyeIcon, 
      title: 'OCR Text Recognition', 
      description: 'Extract text from scanned documents',
      details: 'Convert scanned PDFs and images to searchable text. Make your documents editable and searchable with advanced OCR technology.'
    },
    { 
      icon: ArchiveBoxIcon, 
      title: 'Compress PDFs', 
      description: 'Reduce file size without quality loss',
      details: 'Optimize PDF file sizes for easier sharing and storage. Maintain quality while significantly reducing file size.'
    },
    { 
      icon: ScissorsIcon, 
      title: 'Split PDFs', 
      description: 'Extract specific pages or ranges',
      details: 'Split large PDFs into smaller documents. Extract individual pages or page ranges to create focused, targeted documents.'
    }
  ];

  // Use Cases
  const useCases = [
    {
      icon: AcademicCapIcon,
      title: 'Students & Educators',
      description: 'Convert lecture slides into printable study materials',
      color: 'from-primary to-secondary'
    },
    {
      icon: DocumentDuplicateIcon,
      title: 'Professionals',
      description: 'Transform presentations into clean, shareable documents',
      color: 'from-primary to-secondary'
    },
    {
      icon: PrinterIcon,
      title: 'Note-Takers',
      description: 'Create ink-friendly versions of digital notes',
      color: 'from-primary to-secondary'
    },
    {
      icon: CloudArrowUpIcon,
      title: 'Remote Teams',
      description: 'Process documents quickly without specialized software',
      color: 'from-primary to-secondary'
    }
  ];

  // Technical Capabilities
  const capabilities = [
    { label: 'Cost', value: 'FREE', subtext: 'Forever' },
    { label: 'Privacy', value: '100%', subtext: 'Browser-based' },
    { label: 'Login Required', value: 'NO', subtext: 'Anonymous' },
    { label: 'Processing', value: 'Local', subtext: 'Client-side' }
  ];

  return (
    <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-block mb-4">
            <span className="px-4 py-1.5 rounded-full text-sm font-medium bg-gradient-to-r from-primary/20 to-secondary/20 border border-primary/30 text-primary">
              Powerful PDF Enhancement
            </span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-text-main mb-6">
            Free PDF Tools For
            <span className="block bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Students & Professionals
            </span>
          </h1>
          <p className="text-xl text-text-muted max-w-3xl mx-auto leading-relaxed">
            Process PDFs completely free in your browser. Google Sign in for web access. No uploads. No data collection. 
            Transform lecture slides, merge documents, and optimize for printing—all with complete privacy.
          </p>
        </motion.div>

        {/* Core Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-text-main text-center mb-12">
            Core Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreFeatures.map((feature, i) => {
              const IconComp = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * i }}
                  className="group bg-glass/50 backdrop-blur-sm border border-border-glow rounded-xl p-6 hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary/10"
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                      <IconComp className="h-6 w-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-text-main mb-2">{feature.title}</h3>
                      <p className="text-sm text-text-muted mb-3">{feature.description}</p>
                      <p className="text-xs text-text-muted/70 leading-relaxed">{feature.details}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Technical Capabilities */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-20"
        >
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 border border-border-glow rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-text-main text-center mb-8">Technical Capabilities</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {capabilities.map((cap, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + i * 0.1 }}
                  className="text-center"
                >
                  <div className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent mb-2">
                    {cap.value}
                  </div>
                  <div className="text-sm font-medium text-text-main mb-1">{cap.label}</div>
                  <div className="text-xs text-text-muted">{cap.subtext}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Use Cases */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-text-main text-center mb-12">
            Perfect For Every Workflow
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((useCase, i) => {
              const IconComp = useCase.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 + i * 0.1 }}
                  className="relative group"
                >
                  <div className="bg-glass/50 backdrop-blur-sm border border-border-glow rounded-xl p-6 text-center hover:border-primary/50 transition-all duration-300">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br ${useCase.color} opacity-100 flex items-center justify-center group-hover:opacity-100 transition-opacity`}>
                      <IconComp className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-text-main mb-2">{useCase.title}</h3>
                    <p className="text-sm text-text-muted">{useCase.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="relative overflow-hidden rounded-2xl"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary to-secondary opacity-90"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-10"></div>
          <div className="relative px-8 py-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Process Your PDFs?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Join thousands who trust NotesCrafter for free, private PDF processing
            </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <motion.button
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent('navigateToPage', { detail: { page: 'home', startProcessing: true } }));
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center space-x-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  <span>Start Processing</span>
                </motion.button>
              </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Features;